// Every address the console used before Admin mode moved under `/admin` (ADR 0035, the customer
// app spec 2.8 and 4.2). CRM notes, emails and bookmarks hold these paths, so the table stays for
// good. The root route applies it once, before routing, keeping the search and the hash.

/** Old path prefix, new path prefix. A prefix also carries everything below it. */
export const REDIRECTS: readonly (readonly [string, string])[] = [
  ["/overview", "/admin"],
  ["/live-now", "/admin/live-now"],
  ["/calls", "/admin/calls"],
  ["/messages", "/admin/messages"],
  ["/users", "/admin/people"],
  ["/rep-analytics", "/admin/rep-analytics"],
  ["/campaigns", "/admin/campaigns"],
  ["/assistant", "/admin/ai"],
  ["/imports", "/admin/ai/imports"],
  ["/numbers", "/admin/numbers"],
  ["/voicemail", "/admin/phone-system/voicemail"],
  ["/integrations", "/admin/integrations"],
  ["/providers", "/admin/integrations"],
  ["/policies", "/admin/calling/policies"],
  ["/parallel-dialing", "/admin/calling/parallel-dial"],
  ["/voicemail-drops", "/admin/calling/voicemail-drops"],
  ["/settings", "/admin/calling/dispositions"],
  ["/usage", "/admin/billing/usage"],
  ["/plan", "/admin/billing"],
  ["/audit", "/admin/audit"],
  ["/bug-reports", "/admin/support"],
  ["/coaching", "/admin/coaching"],
];

/**
 * Kite's own pages, which left Admin for the Platform console (owner, 2026-11-01): Live log,
 * Costs, Releases and Demo requests. Their old addresses, and anything below them, land on
 * Admin's Home without the page's search.
 */
const ON_PLATFORM = new Set(["live", "costs", "releases", "demo-requests"]);

/** Old paths whose pages had no addresses below them: a deeper path is not theirs. */
const EXACT = new Set(["/providers"]);

const OLD = new Map(REDIRECTS);

/** The Microsoft 365 consent return of older servers lands on its card. */
const M365 = "/admin/integrations/microsoft-365";

/**
 * The new address for an old one, with its search (`?a=b`, or empty) and hash (with or without
 * `#`). Null when the path is not an old one.
 */
export function redirectFor(pathname: string, search = "", hash = ""): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const parts = path.split("/");
  const head = `/${parts[1] ?? ""}`;
  const moved = parts[1] === "admin" ? parts[2] : parts[1];
  if (moved && ON_PLATFORM.has(moved)) return "/admin";
  const to = OLD.get(head);
  if (!to) return null;
  const rest = parts.slice(2).join("/");
  if (rest && EXACT.has(head)) return null;
  const q = search && !search.startsWith("?") ? `?${search}` : search;
  const h = hash ? (hash.startsWith("#") ? hash : `#${hash}`) : "";
  let target = rest ? `${to}/${rest}` : to;
  if (head === "/integrations" && !rest && /[?&]email_consent=/.test(q)) target = M365;
  return `${target}${q}${h}`;
}
